import { CheckIcon, UserPlus, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useConnectedAccount } from '@/providers/connectedAccount'
import { Button } from '@/ui-components/Button'
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@/ui-components/Dialog'
import { Field, Label } from '@/ui-components/Fieldset'
import { Listbox, ListboxOption } from '@/ui-components/Listbox'
import type { OrganizationUser } from '@/views/Users/types'

export type AddUserToConnectedAccountsModalProps = {
  isOpen: boolean
  onClose: () => void
  user: OrganizationUser | null
  existingConnectedAccountIds: string[]
  onSubmit: (userId: string, vesselIds: string[]) => void
}

const AddUserToConnectedAccountsModal = ({
  isOpen,
  onClose,
  user,
  existingConnectedAccountIds,
  onSubmit,
}: AddUserToConnectedAccountsModalProps) => {
  const { connectedAccounts } = useConnectedAccount()
  const [isSaving, setIsSaving] = useState(false)
  const [selectedConnectedAccountIds, setSelectedConnectedAccountIds] = useState<string[]>(existingConnectedAccountIds)

  useEffect(() => {
    setIsSaving(false)
  }, [isOpen])

  const handleSubmit = () => {
    if (!user || selectedConnectedAccountIds.length === 0) {
      return
    }

    setIsSaving(true)
    onSubmit(user.id, selectedConnectedAccountIds)
    handleClose()
  }

  const handleClose = () => {
    setIsSaving(false)
    setSelectedConnectedAccountIds([])
    onClose()
  }

  if (!user) {
    return null
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <UserPlus className="size-5 text-gray-900 dark:text-white" />
          <DialogTitle>Update Vessel Access</DialogTitle>
        </div>
        <Button
          plain
          onClick={handleClose}
        >
          <X data-slot="icon" />
        </Button>
      </div>

      <DialogBody>
        <div className="space-y-4">
          <div className="space-y-3">
            <p className="text-base/6 font-semibold sm:text-sm/6 text-gray-900 dark:text-white">User</p>
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="font-medium">{user.name}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{user.email}</div>
            </div>
          </div>

          <Field>
            <Label>Vessels</Label>
            <Listbox
              multiple
              value={selectedConnectedAccountIds}
              onChange={setSelectedConnectedAccountIds}
              className="w-full"
              placeholder="Select vessels"
            >
              {connectedAccounts?.map((connectedAccount) => (
                <ListboxOption
                  className="flex items-center gap-2"
                  key={connectedAccount.id}
                  value={connectedAccount.id}
                  disabled={existingConnectedAccountIds.includes(connectedAccount.id)}
                  data-testid={`connected-account-option-${connectedAccount.id}`}
                >
                  {existingConnectedAccountIds.includes(connectedAccount.id) && <CheckIcon className="size-4" />}
                  {connectedAccount.name}
                </ListboxOption>
              ))}
            </Listbox>
          </Field>
        </div>
      </DialogBody>

      <DialogActions>
        <Button
          plain
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          className="data-disabled:opacity-50 data-disabled:cursor-not-allowed"
          disabled={isSaving}
          onClick={handleSubmit}
          data-testid="add-user-to-connected-accounts-confirm"
        >
          {isSaving ? 'Updating...' : 'Update Vessel Access'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddUserToConnectedAccountsModal
